import debounce from 'just-debounce';
import LiveSearchTakeover from './scripts/liveSearchTakeover';

/**
 * @class Header
 * @classdesc The header section is used to handle the header functionality.
 *
 * @extends Core.SectionComponent
 */
export default class Header extends Core.SectionComponent {
  constructor(el, context) {
    super(el, context);
    this.header = this.el.querySelector('[data-header]');
    this.cartCount = this.el.querySelector('[data-cart-count]');
    this.cartDrawerButton = this.el.querySelector('[data-cart-button]');
    this.mobileDrawerButton = this.el.querySelector('[data-menu-button]');
    this.mobileDrawerCloseButton = this.el.querySelector('[data-menu-close-button]');
    this.searchButtons = this.el.querySelectorAll('[data-search-button]');
    this.liveSearchTakeover = this.el.querySelector('[data-live-search-takeover]');
    this.mobileMenu = null;
    this.sticky = this.data.settings.sticky;
    this.showBorder = this.data.settings.show_border;
    this.enable_live_search = this.data.settings.enable_live_search;
    this.live_search_open = false;

    this.events.register(document, 'cart:update', e => {
      this._updateCartCount(e.detail.count);
    });
    this.events.register(document, 'product:add-to-cart:success', e => {
      this._updateCartCount(e.detail.cart.item_count);
    });

    if (this.sticky && this.showBorder === false) {
      this.events.register(document, 'scroll', debounce(() => { this._setStickyShadow(); }, 100, false, true));
    }
  }

  /**
   * On mount, we create the event listeners for the header section.
   */
  onMount() {
    // Event for opening the mobile menu drawer
    this.drawerMenu = this.query('navigation-drawer');
    this.events.register(this.mobileDrawerButton, 'click', e => {
      e.stopPropagation();
      this.drawerMenu.open(this.mobileDrawerButton);

      this.escapeEvent = this.events.register(window, 'keyup', keyevent => {
        if (keyevent.key === 'Escape') {
          this._toggleMenuButton(false);
          this.drawerMenu.close();
          this.mobileDrawerButton.focus();
          this.events.unregister(this.escapeEvent);
        }
      });

      // Set up events to handle keyboard, mouse, and touch events
      this.bodyClickEvent = this.events.register(document, 'click', () => {
        this._closeDrawerMenu();
      });

      this.focusBodyEvent = this.events.register(document.body, 'focusin', () => {
        this._closeDrawerMenu();
      });

      Core.Breakpoints.onChange(() => this._closeDrawerMenu());

      this._toggleMenuButton(true);
    });

    // We need to stop propagation on the events that happen on the drawer menu so that the menu
    // doesn't close from focusin or click events within it.
    this.menuDrawerFocusInEvent = this.events.register(this.drawerMenu.el, 'focusin', event => {
      event.stopPropagation();
    });

    this.menuDrawerClickEvent = this.events.register(this.drawerMenu.el, 'click', event => event.stopPropagation());

    this.events.register(this.mobileDrawerCloseButton, 'click', event => {
      event.stopPropagation();
      this._closeDrawerMenu();
    });

    // Event for opening the cart drawer
    const cartDrawer = Core.Components.getComponent('cart-drawer');
    if (cartDrawer && cartDrawer.data.enabled && !document.body.classList.contains('template-cart')) {
      this.events.register(this.cartDrawerButton, 'click', e => {
        e.stopPropagation();
        e.preventDefault();
        cartDrawer.open(this.cartDrawerButton, false);
      });
    }

    this.headerToolbar = this.query('header-tool-bar');

    this._setHeightVariables();
    this.events.register(window, 'resize', debounce(() => this._setHeightVariables(), 100));

    if (this.enable_live_search) {
      this.liveSearchTakeover = new LiveSearchTakeover({
        liveSearch: this.query('live-search'),
        events: this.events,
        triggerEls: this.searchButtons,
        takeoverEl: this.liveSearchTakeover,
      });
    }
  }

  /**
   * Forces the mobile drawer menu to close
   * @private
   */
  _closeDrawerMenu() {
    this._toggleMenuButton(false);
    this.mobileDrawerButton.focus();
    this.drawerMenu.close();
    this.events.unregister(this.bodyClickEvent);
    this.events.unregister(this.focusBodyEvent);
  }

  /**
   * Toggles the menu and close button in the mobile header.
   * @param {Boolean} open If true, the menu is opening.
   * @private
   */
  _toggleMenuButton(open) {
    if (open) {
      this.mobileDrawerButton.classList.add('hidden');
      this.mobileDrawerCloseButton.classList.remove('hidden');
    } else {
      this.mobileDrawerButton.classList.remove('hidden');
      this.mobileDrawerCloseButton.classList.add('hidden');
    }
  }

  /**
   * Updates the cart count in the header
   *
   * @param {number} count - The cart item count to display.
   * @private
   */
  _updateCartCount(count) {
    this.cartCount.innerHTML = count;

    if (count === 0) {
      this.cartCount.classList.add('header__cart-count--is-zero');
    } else {
      this.cartCount.classList.remove('header__cart-count--is-zero');
    }
  }

  /**
   * Updates the sticky header logic
   *
   * @private
   */
  _setHeightVariables() {
    this.headerSection = document.querySelector('#shopify-section-header');
    let headerHeight = 0;

    if (this.headerToolbar) {
      this.headerToolbarHeight = this.headerToolbar.getHeight();
      this.headerSection.style.setProperty('--announcement-height', `${this.headerToolbarHeight}px`);
      headerHeight += this.headerToolbarHeight;
    }

    headerHeight += this.header.getBoundingClientRect().height;
    this.headerSection.style.setProperty('--header-height', `${headerHeight}px`);
  }

  /**
   * Checks the scroll position and sets the sticky shadow
   *
   * @private
   */
  _setStickyShadow() {
    if (window.scrollY > 100) {
      this.header.classList.add('header--shadow');
    } else {
      this.header.classList.remove('header--shadow');
    }
  }
}
